import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { BoxVariant } from '#pplx/frontend/aether/primitives/BoxPrimitive/BoxVariant.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { useResizeObserver } from '#pplx/frontend/common/hooks/useResizeObserver.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { CommandMenuLoadingSkeleton } from '#pplx/frontend/composer-sdk/components/CommandMenuLoadingSkeleton.js';
import { CommandPanel } from '#pplx/frontend/composer-sdk/components/CommandPanel.js';
import { isTypeaheadOverlayVisible } from '#pplx/frontend/composer-sdk/internal/typeahead/overlayVisibility.js';
import { TypeaheadResolvedRows } from '#pplx/frontend/composer-sdk/internal/typeahead/TypeaheadResolvedRows.js';
import { LexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import classNames from 'classnames';
import { BLUR_COMMAND, COMMAND_PRIORITY_HIGH, KEY_ESCAPE_COMMAND, KEY_TAB_COMMAND } from 'lexical';
import React, { useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { FormattedMessage } from 'react-intl';
/**
 * Height of a single suggestion row: the option's `py-1.5` (12px) plus its ~24px content box
 * (the small-text label line, or a taller trailing badge). Measured at 36px in the rendered
 * dropdown. Used to translate a `maxVisibleResults` row count into a scroll cap for the
 * `suggestDropdown` list.
 */
export const SUGGEST_DROPDOWN_ROW_HEIGHT_PX = 36;
/**
 * Height of a single `comfortable` spotlight row: the roomier `py-2` (16px) plus the same ~24px
 * content box, matching the 40px row of the pre-Composer command palette. Used to translate a
 * `maxVisibleResults` row count into the spotlight body's pinned height.
 */
const SPOTLIGHT_ROW_HEIGHT_PX = 40;
/** Vertical gap between rows in the list (`gap-px`). */
const SUGGEST_DROPDOWN_ROW_GAP_PX = 1;
const LOADING_MESSAGE = (_jsx(FormattedMessage, { defaultMessage: "Loading\u2026", id: "2e2747e5e8", description: "Typeahead results loading state" }));
const NO_RESULTS_MESSAGE = (_jsx(FormattedMessage, { defaultMessage: "No results", id: "6ec711ea15", description: "Typeahead no-results state" }));
function getGroupTitleVariant(variant) {
    switch (variant) {
        case 'commandMenu':
        case 'suggestDropdown':
            return variant;
        case 'inline':
        case 'spotlight':
            return undefined;
        default:
            return assertUnreachableValue(variant);
    }
}
/**
 * Height (px) the row count maps to for the dropdown body: exactly `maxVisibleResults` rows
 * (from the row height and inter-row gap). `suggestDropdown` uses it as a scroll cap; `spotlight`
 * uses it as a pinned constant height (and renders taller `comfortable` rows). Every other case
 * yields `undefined`, leaving the body uncapped. The panel owns the scroll region and turns this
 * into a flush-to-edge scrollbar.
 */
function getBodyHeightPx({ variant, maxVisibleResults, }) {
    switch (variant) {
        case 'suggestDropdown':
        case 'spotlight': {
            if (maxVisibleResults === undefined || maxVisibleResults <= 0) {
                return undefined;
            }
            // TODO(follow-up): Measure rendered dropdown row heights so subtitle rows still honor
            // maxVisibleResults; this fixed single-line height unblocks global command palette work.
            const rowHeightPx = variant === 'spotlight' ? SPOTLIGHT_ROW_HEIGHT_PX : SUGGEST_DROPDOWN_ROW_HEIGHT_PX;
            return (maxVisibleResults * rowHeightPx + (maxVisibleResults - 1) * SUGGEST_DROPDOWN_ROW_GAP_PX);
        }
        case 'commandMenu':
        case 'inline':
            return undefined;
        default:
            return assertUnreachableValue(variant);
    }
}
/** Same edge gap the aether Popover keeps (`collisionPadding`), so both menus stop short of the window edge alike. */
const VIEWPORT_MARGIN_PX = 10;
/**
 * Room (px) between the overlay's anchored edge and the viewport edge it grows towards,
 * exposed as `--radix-popper-available-height`, the variable the command menu panel caps its
 * height by. Inside the Composer the panel sits in a plain anchored div rather than a Radix
 * popper, so nothing else sets it and the panel would fall back to a fixed height that can run
 * past the viewport. Re-measured when the composer box (the anchor's offset parent) resizes,
 * and on scroll or window resize.
 */
function useAnchoredAvailableHeight(placement, enabled) {
    const nodeRef = useRef(null);
    const containerRef = useRef(null);
    const [roomPx, setRoomPx] = useState(null);
    const measure = useCallback(() => {
        const node = nodeRef.current;
        if (!node) {
            return;
        }
        const rect = node.getBoundingClientRect();
        const room = (placement === 'above' ? rect.bottom : window.innerHeight - rect.top) - VIEWPORT_MARGIN_PX;
        setRoomPx(Math.max(0, Math.floor(room)));
    }, [placement]);
    useResizeObserver({ ref: containerRef, onResize: measure, enabled });
    useEffect(() => {
        if (!enabled) {
            return;
        }
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [enabled, measure]);
    const ref = useCallback((node) => {
        nodeRef.current = node;
        containerRef.current = node?.offsetParent instanceof HTMLElement ? node.offsetParent : null;
        measure();
    }, [measure]);
    return {
        ref,
        style: roomPx === null
            ? undefined
            : { '--radix-popper-available-height': `${roomPx}px` },
    };
}
/**
 * Renders the active typeahead state into the right CommandPanel flavor. The
 * engine owns which trigger is active and its results; this component only draws
 * rows, section headers, and the loading/empty affordances. `error` is rendered
 * as hidden for v1 (the panels have no error chrome yet).
 */
export function TypeaheadOverlay({ variant, state, selectedIndex, onHighlight, onSelect, inline, renderSidePanel, placement = 'below', framed, maxVisibleResults, pendingContinuation = 'disabled', emptyMessage, emptyState = 'compact', onDisplayedVariantChange, refreshStatus = 'visible', highlightStyle = 'match', }) {
    const listboxRef = useRef(null);
    const commandMenuSurface = useAnchoredAvailableHeight(placement, variant === 'commandMenu');
    const rows = rowsOf(state);
    const providerSnapshots = state.status === 'loading' || state.status === 'done' || state.status === 'empty'
        ? state.providerSnapshots
        : undefined;
    const isCommandMenuPagePending = variant === 'commandMenu' &&
        (providerSnapshots?.some((snapshot) => snapshot.continuation?.isPending) ?? false);
    const hasSnapshots = providerSnapshots !== undefined && providerSnapshots.length > 0;
    const highlightedIndex = useSnapshotHighlight({
        rows,
        selectedIndex,
        snapshots: providerSnapshots,
        onHighlight,
    });
    const handleReachEnd = useCallback(() => {
        providerSnapshots?.forEach((snapshot) => {
            if (snapshot.continuation?.isPending === false) {
                snapshot.continuation.request('scroll');
            }
        });
    }, [providerSnapshots]);
    const firstContinuationProviderIndex = providerSnapshots?.find((snapshot) => snapshot.continuation?.isPending === false)?.providerIndex;
    // Row count → pixels: a scroll cap for `suggestDropdown`, a pinned constant height for
    // `spotlight`. The panel owns the scroll region so the scrollbar sits flush to its edge.
    const bodyHeightPx = getBodyHeightPx({ variant, maxVisibleResults });
    // The standalone spotlight palette uses roomier rows (its pre-Composer layout); the attached
    // menus keep the tighter default. The loading/empty affordances match the row padding so their
    // text lines up with the results.
    const rowSize = variant === 'spotlight' ? 'comfortable' : 'compact';
    const messagePaddingX = rowSize === 'comfortable' ? 'px-3' : 'px-2';
    const screenReaderOnlyRefresh = isScreenReaderOnlyRefreshStatus({
        variant,
        status: refreshStatus,
    });
    const visible = isTypeaheadOverlayVisible({ variant, state, emptyMessage });
    useLayoutEffect(() => {
        onDisplayedVariantChange?.(visible ? variant : null);
        return () => onDisplayedVariantChange?.(null);
    }, [onDisplayedVariantChange, variant, visible]);
    if (!visible) {
        return null;
    }
    const isInitialCommandMenuLoading = variant === 'commandMenu' &&
        state.status === 'loading' &&
        rows.length === 0 &&
        // Confirmed-empty copy and continuation controls must remain visible during refreshes.
        (providerSnapshots === undefined ||
            providerSnapshots.every((snapshot) => snapshot.continuation === undefined &&
                ((snapshot.status === 'pending' && snapshot.emptyMessage === undefined) ||
                    // Suppressed empty copy must not leave a surfaced menu with only hidden statuses.
                    (screenReaderOnlyRefresh && !shouldShowProviderEmptyMessage(snapshot)))));
    const body = rows.length > 0 ? (_jsx("div", { 
        // Panel variants scroll on their body; only the inline variant scrolls on this listbox.
        ref: variant === 'inline' ? listboxRef : null, role: "listbox", "aria-busy": state.status === 'loading' ? true : undefined, className: "flex w-full flex-col gap-px", children: _jsx(TypeaheadResolvedRows, { highlightStyle: highlightStyle, rows: rows, selectedIndex: highlightedIndex, rowSize: variant === 'commandMenu' ? 'spacious' : rowSize, separateSectionHeaders: variant === 'spotlight', groupTitleVariant: getGroupTitleVariant(variant), onHighlight: onHighlight, onSelect: onSelect, listboxRef: listboxRef }) })) : isInitialCommandMenuLoading ? (_jsx(CommandMenuLoadingSkeleton, {})) : hasSnapshots ? null : state.status === 'loading' ? (_jsx(TypeaheadStatusMessage, { paddingX: messagePaddingX, children: LOADING_MESSAGE })) : (_jsx(TypeaheadEmptyMessage, { presentation: emptyState, paddingX: messagePaddingX, children: emptyMessage ?? NO_RESULTS_MESSAGE }));
    // Controls sit outside the selectable listbox: they never become rows or change flat indexes.
    // Keep the row subtree mounted while snapshots refresh so a page append retains its scroll.
    const content = (_jsxs(_Fragment, { children: [body, !isInitialCommandMenuLoading &&
                providerSnapshots?.map((snapshot) => (_jsx(ProviderSnapshotStatus, { snapshot: snapshot, paddingX: messagePaddingX, emptyState: emptyState, screenReaderOnlyRefresh: screenReaderOnlyRefresh, hideContinuation: isCommandMenuPagePending, enterFromQuery: snapshot.providerIndex === firstContinuationProviderIndex, showSectionTitle: providerSnapshots.length > 1 ||
                        rows.some((row) => row.providerIndex !== snapshot.providerIndex) }, snapshot.providerIndex))), !isInitialCommandMenuLoading &&
                hasSnapshots &&
                state.status === 'loading' &&
                providerSnapshots.every((snapshot) => snapshot.status === 'settled') && (_jsx(TypeaheadStatusMessage, { paddingX: messagePaddingX, role: "status", screenReaderOnly: screenReaderOnlyRefresh, children: LOADING_MESSAGE }))] }));
    switch (variant) {
        case 'commandMenu': {
            let showPendingContinuation;
            switch (pendingContinuation) {
                case 'disabled':
                    showPendingContinuation = isCommandMenuPagePending;
                    break;
                case 'hidden':
                    showPendingContinuation = false;
                    break;
                default:
                    return assertUnreachableValue(pendingContinuation);
            }
            // Floating menus need their own surface and a 4px gap; embedded menus inherit the frame.
            // Both retain padding to compensate for CommandMenuPanel's negative margin.
            // TODO(follow-up): bake the surface into CommandMenuPanel like InlinePanel/
            // SuggestDropdownPanel do, and add a surfaceless variant to the aether Popover for the
            // legacy command menu, so all three panels self-surface and this wrap can go away.
            return (_jsx(BoxPrimitive, { ref: commandMenuSurface.ref, style: commandMenuSurface.style, noBorder: framed, padding: "sm", rounded: framed ? undefined : 'xl', bg: framed ? undefined : BoxVariant.raised, className: classNames(!framed && ['shadow-md', placement === 'above' ? 'mb-1' : 'mt-1']), children: _jsxs(CommandPanel, { variant: "commandMenu", framed: framed, sidePanel: renderSidePanel?.(state), bodyRef: listboxRef, onReachEnd: hasSnapshots ? handleReachEnd : undefined, children: [content, showPendingContinuation && (_jsx(Button, { variant: "text", size: "small", disabled: true, track: false, children: LOADING_MESSAGE }))] }) }));
        }
        case 'suggestDropdown':
            // Visible suggestion content keeps the attached-panel edge and divider.
            return (_jsx(CommandPanel, { variant: "suggestDropdown", placement: placement === 'above' ? 'top' : 'bottom', maxBodyHeightPx: bodyHeightPx, bodyRef: listboxRef, onReachEnd: hasSnapshots ? handleReachEnd : undefined, children: content }));
        case 'spotlight':
            return (_jsx(CommandPanel, { variant: "spotlight", bodyHeightPx: bodyHeightPx, bodyRef: listboxRef, onReachEnd: hasSnapshots ? handleReachEnd : undefined, children: content }));
        case 'inline':
            return (_jsx(CommandPanel, { variant: "inline", isTop: inline?.isTop, viewportCollisionLeftOffsetPx: inline?.viewportCollisionLeftOffsetPx, fitContent: inline?.fitContent, onReachEnd: hasSnapshots ? handleReachEnd : undefined, children: content }));
    }
}
/** Retains an opted-in provider's selected item when aggregated rows change its flat index. */
function useSnapshotHighlight({ rows, selectedIndex, snapshots, onHighlight, }) {
    const previous = useRef({ rows, selectedIndex, snapshots });
    let highlightedIndex = selectedIndex;
    if (previous.current.selectedIndex === selectedIndex && previous.current.rows !== rows) {
        const selectedRow = previous.current.rows[previous.current.selectedIndex ?? -1];
        const selectedSnapshot = previous.current.snapshots?.find((snapshot) => snapshot.providerIndex === selectedRow?.providerIndex);
        if (selectedRow !== undefined && selectedSnapshot?.highlightKey !== undefined) {
            const currentSnapshot = snapshots?.find((snapshot) => snapshot.highlightKey === selectedSnapshot.highlightKey);
            const retainedIndex = currentSnapshot === undefined
                ? -1
                : rows.findIndex((row) => row.providerIndex === currentSnapshot.providerIndex &&
                    row.item.id === selectedRow.item.id);
            highlightedIndex = retainedIndex >= 0 ? retainedIndex : null;
        }
    }
    // Synchronize before paint so keyboard selection and row scrolling never observe the old index.
    useLayoutEffect(() => {
        previous.current = { rows, selectedIndex: highlightedIndex, snapshots };
        // A refresh can move or remove the item. Clearing a removed identity lets the keyboard
        // owner apply the provider's initial-selection policy instead of selecting its replacement.
        if (highlightedIndex !== selectedIndex) {
            onHighlight(highlightedIndex);
        }
    }, [rows, highlightedIndex, selectedIndex, snapshots, onHighlight]);
    return highlightedIndex;
}
function shouldShowProviderEmptyMessage({ hasRows, emptyMessage, status, continuation, }) {
    return (!hasRows &&
        emptyMessage !== null &&
        (emptyMessage !== undefined || (status === 'settled' && continuation === undefined)));
}
function ProviderSnapshotStatus({ snapshot, paddingX, emptyState, showSectionTitle, enterFromQuery, screenReaderOnlyRefresh, hideContinuation, }) {
    const titleId = useId();
    // Standalone overlays have no editor; their control remains an ordinary button.
    const editor = useContext(LexicalComposerContext)?.[0];
    const groupRef = useRef(null);
    const buttonRef = useRef(null);
    const { continuation, emptyMessage } = snapshot;
    const isContinuationPending = continuation?.isPending === true;
    const showContinuation = continuation !== undefined && !hideContinuation;
    useEffect(() => {
        if (editor === undefined || !showContinuation)
            return;
        return mergeRegister(
        // Moving from the query into this control stays within the active menu.
        editor.registerCommand(BLUR_COMMAND, (event) => event.relatedTarget instanceof Node &&
            groupRef.current?.contains(event.relatedTarget) === true, COMMAND_PRIORITY_HIGH), editor.registerCommand(KEY_TAB_COMMAND, (event) => {
            if (event === null || event.shiftKey || isContinuationPending || !enterFromQuery) {
                return false;
            }
            // Enter the continuation before Tab can select a row or leave the editor for Submit.
            event.preventDefault();
            buttonRef.current?.focus();
            return true;
        }, COMMAND_PRIORITY_HIGH));
    }, [editor, showContinuation, isContinuationPending, enterFromQuery]);
    let isPending;
    switch (snapshot.status) {
        case 'pending':
            isPending = true;
            break;
        case 'settled':
            isPending = false;
            break;
        default:
            return assertUnreachableValue(snapshot.status);
    }
    const showEmpty = shouldShowProviderEmptyMessage(snapshot);
    if (!showEmpty && !isPending && continuation === undefined) {
        return null;
    }
    const hasTitle = showSectionTitle && snapshot.sectionTitle !== null && snapshot.sectionTitle !== undefined;
    return (_jsxs("div", { ref: groupRef, role: "group", "aria-labelledby": hasTitle ? titleId : undefined, "aria-busy": isPending || continuation?.isPending || undefined, 
        // Retain the named busy group without leaving an empty visual section.
        className: !showEmpty && !showContinuation && (!isPending || screenReaderOnlyRefresh)
            ? 'sr-only'
            : undefined, onBlur: (event) => {
            if (editor === undefined ||
                event.relatedTarget === editor.getRootElement() ||
                (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget))) {
                return;
            }
            // Only entering this control bypasses editor blur. Leaving it keeps normal dismissal.
            editor.dispatchCommand(BLUR_COMMAND, event.nativeEvent);
        }, children: [hasTitle && (_jsx("div", { id: titleId, className: classNames('text-tertiary py-1 text-xs', paddingX), children: snapshot.sectionTitle })), showEmpty && (_jsx(TypeaheadEmptyMessage, { presentation: emptyState, paddingX: paddingX, children: emptyMessage ?? NO_RESULTS_MESSAGE })), isPending && (_jsx(TypeaheadStatusMessage, { paddingX: paddingX, role: "status", screenReaderOnly: screenReaderOnlyRefresh, children: LOADING_MESSAGE })), showContinuation && (_jsx(Button, { ref: buttonRef, variant: "text", size: "small", disabled: continuation.isPending, track: false, 
                // Pointer pagination must leave the editor focused so blur does not dismiss the menu.
                __dangerousHtmlProps: {
                    onMouseDown: (event) => {
                        if (event.button === 0)
                            event.preventDefault();
                    },
                    onKeyDown: (event) => {
                        if (editor === undefined)
                            return;
                        if (event.key === 'Escape') {
                            event.preventDefault();
                            editor.focus(() => editor.dispatchCommand(KEY_ESCAPE_COMMAND, event.nativeEvent));
                        }
                        else if (event.key === 'Tab' && event.shiftKey && enterFromQuery) {
                            // Only the first control exits to the query; later controls keep native tab order.
                            event.preventDefault();
                            // The query still owns the selection. Dirtying it with editor.focus() schedules
                            // an inline anchor reattachment that can blur this control after the next Tab.
                            editor.getRootElement()?.focus();
                        }
                    },
                }, onClick: () => {
                    const root = editor?.getRootElement();
                    const selection = root?.ownerDocument.getSelection();
                    // Native caret movement can precede Lexical's selectionchange. Preserve the live
                    // query range when returning focus, before pending controls disappear.
                    if (root &&
                        selection &&
                        root.contains(selection.anchorNode) &&
                        root.contains(selection.focusNode)) {
                        root.focus();
                    }
                    else {
                        editor?.focus();
                    }
                    continuation.request('button');
                }, children: continuation.isPending ? (LOADING_MESSAGE) : (_jsx(FormattedMessage, { defaultMessage: "Load more", id: "1c6651108b", description: "Button label for loading more typeahead results" })) }))] }));
}
function TypeaheadEmptyMessage({ children, presentation, paddingX, }) {
    switch (presentation) {
        case 'compact':
            return _jsx(TypeaheadStatusMessage, { paddingX: paddingX, children: children });
        case 'spacious':
            return (_jsx(BoxPrimitive, { padding: "sm", children: _jsx(TextPrimitive, { variant: "small", color: "light", padding: { x: 'sm', y: 'md' }, rounded: "lg", children: children }) }));
        default:
            return assertUnreachableValue(presentation);
    }
}
/** Keep pending and compact empty rows aligned with the same result-row padding. */
function TypeaheadStatusMessage({ children, paddingX, role, screenReaderOnly = false, }) {
    return (_jsx("div", { role: role, className: screenReaderOnly ? 'sr-only' : classNames('text-tertiary py-1.5 text-sm', paddingX), children: children }));
}
function isScreenReaderOnlyRefreshStatus({ variant, status, }) {
    if (variant !== 'commandMenu') {
        return false;
    }
    switch (status) {
        case 'visible':
            return false;
        case 'screenReaderOnly':
            return true;
        default:
            return assertUnreachableValue(status);
    }
}
const rowsOf = (state) => state.status === 'done' || state.status === 'loading' ? state.rows : [];
//# sourceMappingURL=TypeaheadOverlay.js.map